import { useCallback, useMemo, useState } from 'react';
import type { ForceGraphNode, MemoryNodeDetail, RelatedNode } from '@/workshop/components/shapes/memory-frame/types';
import { MemoryFrame } from '@/workshop/components/shapes/memory-frame/memory-frame';
import { MemoryNodePanel } from '@/workshop/components/panels/memory-node-panel';
import { MEDIUM_GRAPH } from '@/workshop/components/shapes/memory-frame/mock-data';
import { deriveRelatedNodes } from './derive-related-nodes';

// ---------------------------------------------------------------------------
// Mock node-level stats — enriches specific nodes with source_type, mention_count
// and updated_at to showcase the node stats section. Workshop only.
// ---------------------------------------------------------------------------

const MOCK_NODE_STATS: Record<string, Partial<MemoryNodeDetail>> = {
  alice: {
    source_type: 'auto_discovered',
    mention_count: 14,
    updated_at: new Date(Date.now() - 2 * 86_400_000).toISOString(),
  },
  denker: {
    source_type: 'manual',
    mention_count: 8,
    updated_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  },
  bob: {
    source_type: 'auto_discovered',
    mention_count: 6,
    updated_at: new Date(Date.now() - 10 * 86_400_000).toISOString(),
  },
};

// ---------------------------------------------------------------------------
// Mock provenance overlay — enriches specific nodes with claim text + sources
// to showcase the full provenance UI design. Workshop only.
// ---------------------------------------------------------------------------

const MOCK_PROVENANCE: Record<string, Partial<RelatedNode>[]> = {
  alice: [
    {
      claimText: 'Alice leads the engineering team and is the primary contact for Denker AI',
      confidence: 0.92,
      usageCount: 7,
      correctionCount: 0,
      sources: [
        {
          type: 'conversation',
          confidence: 0.95,
          metadata: {
            llm_reasoning: 'User repeatedly refers to Alice as the engineering lead in task discussions',
            message_snippet: 'Ask Alice — she owns the technical roadmap for this quarter',
            entity_extraction_confidence: 0.91,
            similar_mentions: 5,
          },
          timestamp: new Date(Date.now() - 2 * 86_400_000).toISOString(),
          id: 'conv-abc-123',
        },
        {
          type: 'document_extraction',
          confidence: 0.88,
          metadata: {
            llm_reasoning: 'Meeting notes explicitly list Alice Chen as Engineering Lead',
            message_snippet: 'Engineering Lead: Alice Chen — owns delivery milestones',
            entity_extraction_confidence: 0.94,
            similar_mentions: 2,
          },
          timestamp: new Date(Date.now() - 5 * 86_400_000).toISOString(),
          id: 'doc-meeting-notes',
        },
      ],
    },
  ],
};

/**
 * Merges base RelatedNodes from the graph with any mock provenance overlay
 * for that node. Overlay entries are matched by position (index).
 */
function applyProvenance(nodeId: string, related: RelatedNode[]): RelatedNode[] {
  const overlay = MOCK_PROVENANCE[nodeId];
  if (!overlay) return related;
  return related.map((r, i) => (overlay[i] ? { ...r, ...overlay[i] } : r));
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10" data-testid={`memory-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap gap-6">{children}</div>
    </div>
  );
}

function enrichNode(node: MemoryNodeDetail): MemoryNodeDetail {
  return { ...node, ...MOCK_NODE_STATS[node.id] };
}

export function MemoryPage() {
  const [selectedNode, setSelectedNode] = useState<MemoryNodeDetail | null>(null);
  const graphData = MEDIUM_GRAPH;

  const enrichedNode = useMemo(
    () => (selectedNode ? enrichNode(selectedNode) : null),
    [selectedNode],
  );

  const relatedNodes = useMemo(
    () =>
      enrichedNode
        ? applyProvenance(
            enrichedNode.id,
            deriveRelatedNodes(enrichedNode.id, graphData.links, graphData.nodes),
          )
        : [],
    [enrichedNode, graphData.links, graphData.nodes],
  );

  const handleNodeClick = useCallback(
    (node: MemoryNodeDetail) => setSelectedNode(node),
    [],
  );

  const handlePanelClose = useCallback(() => setSelectedNode(null), []);

  const handleNodeSelect = useCallback(
    (id: string) => {
      const next = graphData.nodes.find((n) => n.id === id) ?? null;
      setSelectedNode(next as MemoryNodeDetail | null);
    },
    [graphData.nodes],
  );

  return (
    <div className="p-8" data-testid="memory-page">
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Memory Frame</h1>
        <p className="mt-1 text-sm text-white/40">
          Interactive knowledge graph with force simulation. Click a node to inspect
          its details in the panel. Try clicking <strong className="text-white/60">Alice Chen</strong> to
          see rich provenance data.
        </p>
      </div>

      {/* Demo: frame + panel side by side */}
      <Section title="Node Detail Panel">
        <div className="flex items-start gap-3">
          <div className="h-[480px] w-[520px]">
            <MemoryFrame
              data-testid="memory-frame-interactive"
              scenario="medium"
              onNodeClick={handleNodeClick as (node: ForceGraphNode) => void}
            />
          </div>
          {enrichedNode ? (
            <MemoryNodePanel
              node={enrichedNode}
              relatedNodes={relatedNodes}
              onClose={handlePanelClose}
              onNodeSelect={handleNodeSelect}
            />
          ) : (
            <div className="flex h-[480px] w-80 shrink-0 items-center justify-center rounded-2xl border border-glass-stroke-subtle text-[11px] text-muted">
              Click a node to inspect
            </div>
          )}
        </div>
      </Section>

      {/* Showcase: standalone frame sizes */}
      <Section title="Frame Sizes">
        <MemoryFrame data-testid="memory-frame-small" scenario="small" title="Small Graph" />
        <MemoryFrame data-testid="memory-frame-large" scenario="large" title="Large Graph" />
      </Section>
    </div>
  );
}
